import { SearchArticlesItem } from '../../common/api/searchArticles';
import { selectGenerateAnswerResult } from '../../common/selectors/answersGeneration';
import { selectSearchInputType, selectSearchSessionId } from '../../common/selectors/helpCenter';
import { selectEditorType, selectSearchConfig } from '../../common/selectors/homepage';
import { selectRequiresSupport } from '../../common/selectors/supportRequest';
import { Page } from '../../common/types';
import { biLogger } from '../biLogger';
import { createSearchResultsSolutionInfo } from '../solutionInfo';
import { BiEventLogger } from '../types';
import { getSummarizedAnswersGenFieldsBi, pageEnumToString, solutionInfoPageMapper } from '../utils';
import { convertArticleType } from '../utils/articles';

type SearchResultsBiData = {
  solutions: SearchArticlesItem[];
  query: string;
  currentPage?: Page;
};

const determineAnswerType = (
  solutions: SearchArticlesItem[],
  requiresSupport: boolean | undefined,
  summarizedAnswerFields: ReturnType<typeof getSummarizedAnswersGenFieldsBi>,
): string => {
  if (requiresSupport) {
    return 'support request';
  }
  // Empty solutions always means 'empty'
  if (!solutions || solutions.length === 0) {
    return 'empty';
  }
  // Any AG attempt (regular, prohibited, or failed) should return 'generated answer'
  if (summarizedAnswerFields?.summarizedAnswerType) {
    return 'generated answer';
  }
  return 'list';
};

export const logHelpWidgetSearchResultsPresented: BiEventLogger<SearchResultsBiData> = async (
  state,
  { solutions, query, currentPage },
) => {
  const requiresSupport = selectRequiresSupport(state);
  const searchSessionId = selectSearchSessionId(state);
  const searchInputType = selectSearchInputType(state);
  const editorType = selectEditorType(state);
  const searchConfig = selectSearchConfig(state);
  const answerGenResult = selectGenerateAnswerResult(state);

  const widgetPageType = currentPage ? pageEnumToString(currentPage) : 'search_results';

  const solutionInfo =
    currentPage && solutionInfoPageMapper[currentPage]
      ? solutionInfoPageMapper[currentPage](state)
      : createSearchResultsSolutionInfo(
          solutions.map((solution) => ({
            id: solution.id,
            title: solution.title,
            url: solution.url,
            distance: solution.distance,
            articleType: solution.type ? convertArticleType(solution.type) : undefined,
          })),
        );

  const presentedContent = JSON.stringify(solutionInfo ?? null);

  const searchContext = JSON.stringify({
    editorType,
    categoriesIds: searchConfig?.filters?.categoriesIds,
    excludedCategoriesIds: searchConfig?.filters?.excludedCategoriesIds,
  });

  const summarizedAnswerFields = getSummarizedAnswersGenFieldsBi(answerGenResult);
  const answerType = determineAnswerType(solutions, requiresSupport, summarizedAnswerFields);

  const searchMethod = 'Knowledge Agent';

  const biSummarizedFields = {
    summarized_answer_based_on: summarizedAnswerFields?.summarizedAnswerBasedOn,
    summarized_answer_text: summarizedAnswerFields?.summarizedAnswerText,
    summarized_answer_type:
      requiresSupport || answerType === 'empty' || answerType === 'list'
        ? undefined
        : summarizedAnswerFields?.summarizedAnswerType,
    summarized_answer_words_count: summarizedAnswerFields?.summarizedAnswerWordsCount,
    summarized_answer_media_details: summarizedAnswerFields?.summarizedAnswerMediaDetails,
  };

  await biLogger.helpWidgetSearchResultsNew(state, {
    searched_text: query,
    is_support_request: requiresSupport,
    widget_page_type: widgetPageType,
    presented_content: presentedContent,
    input_type: searchInputType,
    search_method: searchMethod,
    search_session_id: searchSessionId,
    answer_type: answerType,
    search_context: searchContext,
    ...biSummarizedFields,
  });
};
